// app/javascript/controllers/topology_editor_controller.js
import { Controller } from "@hotwired/stimulus"

function safeParseArray(v) {
  try {
    const x = typeof v === "string" ? JSON.parse(v) : v
    return Array.isArray(x) ? x : []
  } catch {
    return []
  }
}

export default class extends Controller {
  static values = {
    switches: Array,
    editing: Boolean,
    projectId: Number,
    projectPath: String
  }

  static targets = [
    "row", "switchLabel", "switchSelect", "portCell",
    "switchModel", "summary", "status",
    "editButton", "exportButton", "saveButton", "cancelButton"
  ]

  connect() {
    const raw = this.hasSwitchesValue ? this.switchesValue : []
    const list = Array.isArray(raw) ? raw : safeParseArray(raw)
    this.switchMap = this.buildSwitchMap(list)

    this.pending = new Map()

    this._recalcFromRows()
    this.rowTargets.forEach(tr => this._initRow(tr))

    if (!this.originalMap || this.originalMap.size === 0) {
      this.originalMap = this._snapshotAssignments()
    }

    this.onStreamEnd = (event) => {
      const targets = Array.from(event.target.querySelectorAll("[target]"))
        .map(el => el.getAttribute("target"))
      if (targets.includes("topology-table")) {
        this.setEditing(false)
      }
    }
    document.addEventListener("turbo:after-stream-render", this.onStreamEnd)

    this.debugEnabled = this._shouldEnableDebug()
    this._debug("connect", { rows: this.rowTargets.length })
  }

  disconnect() {
    if (this.onStreamEnd) {
      document.removeEventListener("turbo:after-stream-render", this.onStreamEnd)
    }
  }

  toggleEdit(event) {
    if (event) event.preventDefault()
    this.setEditing(!this.editingValue)
  }

  setEditing(editing) {
    this.editingValue = !!editing

    this.rowTargets.forEach(tr => {
      const label = tr.querySelector('[data-topology-editor-target="switchLabel"]')
      const select = tr.querySelector('[data-topology-editor-target="switchSelect"]')
      if (!label || !select) return

      if (this.editingValue) {
        label.classList.add("hidden")
        select.classList.remove("hidden")
      } else {
        label.classList.remove("hidden")
        select.classList.add("hidden")
      }
    })

    if (this.hasExportButtonTarget) {
      this.exportButtonTarget.classList.toggle("hidden", this.editingValue)
    }
    if (this.hasEditButtonTarget) {
      this.editButtonTarget.classList.toggle("hidden", this.editingValue)
    }
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.toggle("hidden", !this.editingValue)
    }
    if (this.hasCancelButtonTarget) {
      this.cancelButtonTarget.classList.toggle("hidden", !this.editingValue)
    }

    this.updateStatus()
    if (!this.editingValue) this._clearHash()
  }

  assignSwitch(event) {
    event.preventDefault()
    const select = event.currentTarget
    const dropdownRoot = select.closest('[data-topology-editor-target="switchSelect"]')
    const tr = dropdownRoot?.closest("tr") || select.closest("tr")
    if (!tr) return
    const apId = tr.dataset.apId
    const prevId = tr.dataset.currentSwitchId || ""
    const prevMode = tr.dataset.assignmentMode || (prevId ? "assigned" : "auto")
    const hasSwitchId = Object.prototype.hasOwnProperty.call(select.dataset, "switchId")
    const token = hasSwitchId ? (select.dataset.switchId || "") : (select.value || "")

    let nextId = token
    let nextMode = "assigned"
    if (token === "__AUTO__") {
      nextId = ""
      nextMode = "auto"
    } else if (!token) {
      nextId = ""
      nextMode = "manual_unassigned"
    }

    if (!apId || (prevId === nextId && prevMode === nextMode)) return

    const beforeSnapshot = this._snapshotAssignmentsWithMode()
    this._debug("assignSwitch:start", { apId, prevId, prevMode, token, nextId, nextMode })

    tr.dataset.currentSwitchId = nextId
    tr.dataset.assignmentMode = nextMode

    this._recalcFromRows()
    this.rowTargets.forEach(row => this._updateRowUI(row))
    this._lastInlineErrorKey ||= null

    const swId = tr.dataset.currentSwitchId || ""
    const apNeed = tr.dataset.apPoe || ""
    const apSupply = tr.dataset.apPowerSupply || ""
    let toastErr = null

    if (swId && this.switchMap.has(swId)) {
      const sw = this.switchMap.get(swId)
      const poeErr = this._checkPoeCompatibility({ poeStandard: apNeed, powerSupply: apSupply }, sw)
      const portErr = this._checkPortCapacity(sw)
      const pwrErr = this._checkPowerBudget(sw)
      toastErr = poeErr || portErr || pwrErr
    }

    if (toastErr) {
      const key = `${apId}->${swId}:${toastErr}`
      if (this._lastInlineErrorKey !== key) {
        this._lastInlineErrorKey = key
        this.setStatus(toastErr, "error", { toast: true })
      }
    } else {
      this._lastInlineErrorKey = null
    }

    if (nextMode === "assigned") {
      this.pending.set(apId, { mode: "assigned", switchId: nextId })
    } else if (nextMode === "manual_unassigned") {
      this.pending.set(apId, { mode: "manual_unassigned", switchId: null })
    } else {
      this.pending.set(apId, { mode: "auto", switchId: null })
    }
    this.renderSummary()
    this.setStatus(`Pending changes: ${this.pending.size}`)

    const changed = this._diffAssignmentsWithMode(beforeSnapshot)
    const changedNonTarget = changed.filter(c => c.apId !== apId)
    if (changedNonTarget.length > 0) {
      this._debug("assignSwitch:unexpected-mutation", { apId, changed, changedNonTarget })
      this._restoreAssignmentsWithMode(beforeSnapshot, apId)
      this._recalcFromRows()
      this.rowTargets.forEach(row => this._updateRowUI(row))
      this.setStatus("Detected unexpected row mutation. Restored affected rows.", "error", { toast: true })
      return
    }

    this._debug("assignSwitch:done", { apId, changed, pending: this.pending.size })
  }

  async saveTopology(event) {
    if (event) event.preventDefault()
    if (!this.pending || this.pending.size === 0) {
      this.setEditing(false)
      return
    }

    const assignments = []
    const unassign = []
    for (const [apId, change] of this.pending.entries()) {
      if (change?.mode === "assigned" && change.switchId) {
        assignments.push({ device_id: apId, switch_id: change.switchId })
      } else if (change?.mode === "manual_unassigned") {
        unassign.push(apId)
      }
    }
    try {
      const token = document.querySelector("meta[name='csrf-token']")?.content
      const res = await fetch(`/projects/${this.projectIdValue}/topologies/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          "Accept": "text/vnd.turbo-stream.html, application/json, text/html"
        },
        body: JSON.stringify({ assignments, unassign })
      })

      if (res.ok) {
        const ct = res.headers.get("content-type") || ""
        if (ct.includes("text/vnd.turbo-stream.html")) {
          const html = await res.text()
          document.documentElement.insertAdjacentHTML("beforeend", html)
          this.pending.clear()
          this.setEditing(false)
          return
        }
        if (ct.includes("application/json")) {
          const data = await res.json()
          if (Array.isArray(data.switches)) {
            this.switchMap = this.buildSwitchMap(data.switches)
          }
          if (data.html) {
            const frame = document.getElementById("topology-table")
            if (frame) frame.innerHTML = data.html

            requestAnimationFrame(() => {
              this._recalcFromRows()
              this.rowTargets.forEach(tr => this._initRow(tr))
              this.rowTargets.forEach(tr => this._updateRowUI(tr))
              this.originalMap = this._snapshotAssignments()
            })
          }
          this._showServerResult(data)
          this.pending.clear()
          this.setEditing(false)
          return
        }
        this.pending.clear()
        this.setEditing(false)
        window.location.reload()
        return
      } else {
        try {
          const ct = res.headers.get("content-type") || ""
          let msg = `Save failed: ${res.status} ${res.statusText}`
          if (ct.includes("application/json")) {
            const j = await res.json()
            const e = j.errors || j.error || j.message
            if (e) msg = Array.isArray(e) ? e.join(", ") : String(e)
          } else {
            const t = await res.text()
            if (t) msg = t.slice(0, 300)
          }
          this.setStatus(msg)
        } catch {
          this.setStatus("Save failed.")
        }
        return
      }
    } catch (err) {
      this.setStatus(`Save failed: ${err.message}`)
    }
  }

  showNotification(message) {
    if (!message) return
    const container = this.element.querySelector('#topologies-toast') || document.getElementById('topologies-toast')
    if (!container) { console.warn('[toast] #topologies-toast missing'); return }

    const url = `/projects/${this.projectIdValue}/topologies/toast`
    const params = new URLSearchParams({ message: String(message), type: 'error' })

    fetch(`${url}?${params.toString()}`, {
      headers: { 'Accept': 'text/html' },
      credentials: 'same-origin'
    })
      .then(res => res.text())
      .then(html => {
        if (!html) return
        const tmp = document.createElement('div')
        tmp.innerHTML = html.trim()
        const item = tmp.querySelector('.toast__content')
        if (!item) return
        item.classList.add('toast__content--error')
        container.appendChild(item)
        item.style.animation = 'flashMessage 3s linear'
        item.querySelector('.toast__close')?.addEventListener('click', () => item.remove())
        setTimeout(() => item.remove(), 3000)
      })
      .catch(err => console.warn('[toast] fetch failed:', err))
  }

  renderSummary() {
    if (!this.hasSummaryTarget) return
    const list = Array.from(this.switchMap?.values?.() || [])
    if (list.length === 0) {
      this.summaryTarget.innerHTML = ""
      return
    }
    const html = `
      <div class="muted" style="font-size:14px;margin-top:.25rem;">Switch usage:</div>
      <div style="display:flex;flex-wrap:wrap;gap:.5rem;margin-top:.25rem;">
        ${list.map(sw => {
      const over = Number(sw.used || 0) > Number(sw.total || 0)
      return `
            <div class="chip ${over ? "chip--error" : ""}" title="${sw.id}">
              <strong>${sw.id}</strong> ${sw.used || 0}/${sw.total || 0}
              ${over ? ' <span aria-label="over">!</span>' : ""}
            </div>`
    }).join("")}
      </div>
    `
    this.summaryTarget.innerHTML = html
  }

  updateStatus() {
    if (!this.hasStatusTarget) return
    if (this.editingValue) {
      const n = this.pending?.size || 0
      this.statusTarget.textContent = n ? `Pending changes: ${n}` : ""
    } else {
      this.statusTarget.textContent = ""
    }
  }

  setStatus(msg, type = 'info', { toast = false } = {}) {
    if (this.hasStatusTarget) this.statusTarget.textContent = msg || ""
    if (toast && msg) this.showNotification(msg, type)
  }

  _initRow(tr) {
    const rawCurrent = tr.dataset.currentSwitchId || ""
    const current = rawCurrent === "__AUTO__" ? "" : rawCurrent
    tr.dataset.currentSwitchId = current
    const select = tr.querySelector('[data-topology-editor-target="switchSelect"]')
    if (select && select.tagName === "SELECT") select.value = current

    const portCell = tr.querySelector('[data-topology-editor-target="portCell"]')
    if (portCell && current && this.switchMap?.has(current)) {
      const existing = (portCell.textContent || "").trim()
      if (existing === "" || existing === "-" || existing === "—") {
        const sw = this.switchMap.get(current)
        portCell.textContent = `${sw.used}/${sw.total || 0}`
        this._toggleOverCapacityClass(portCell, (sw.used || 0) > (sw.total || 0))
      }
    }

    const label = tr.querySelector('[data-topology-editor-target="switchLabel"]')
    const switchModel = tr.querySelector('[data-topology-editor-target="switchModel"]')
    const dropdownText = tr.querySelector('[data-dropdown-input-target="text"]')
    if (switchModel && current && this.switchMap?.has(current)) {
      switchModel.textContent = this.switchMap.get(current).model || "-"
    }
    if (dropdownText) dropdownText.textContent = current || "-"

    if (label && select) {
      if (this.editingValue) {
        label.classList.add("hidden")
        select.classList.remove("hidden")
      } else {
        label.classList.remove("hidden")
        select.classList.add("hidden")
      }
    }
  }

  _snapshotAssignments() {
    return new Map(
      this.rowTargets.map(tr => [tr.dataset.apId, tr.dataset.currentSwitchId || ""])
    )
  }

  _snapshotAssignmentsWithMode() {
    return new Map(
      this.rowTargets.map(tr => [
        tr.dataset.apId,
        {
          switchId: tr.dataset.currentSwitchId || "",
          mode: tr.dataset.assignmentMode || ((tr.dataset.currentSwitchId || "") ? "assigned" : "auto")
        }
      ])
    )
  }

  _diffAssignmentsWithMode(before) {
    const changed = []
    this.rowTargets.forEach(tr => {
      const apId = tr.dataset.apId
      const prev = before.get(apId) || { switchId: "", mode: "auto" }
      const curr = {
        switchId: tr.dataset.currentSwitchId || "",
        mode: tr.dataset.assignmentMode || ((tr.dataset.currentSwitchId || "") ? "assigned" : "auto")
      }
      if (prev.switchId !== curr.switchId || prev.mode !== curr.mode) {
        changed.push({ apId, before: prev, after: curr })
      }
    })
    return changed
  }

  _restoreAssignmentsWithMode(before, keepApId = null) {
    this.rowTargets.forEach(tr => {
      const apId = tr.dataset.apId
      if (keepApId && apId === keepApId) return
      const prev = before.get(apId)
      if (!prev) return
      tr.dataset.currentSwitchId = prev.switchId || ""
      tr.dataset.assignmentMode = prev.mode || ((prev.switchId || "") ? "assigned" : "auto")
    })
  }

  _debug(message, payload = null) {
    if (!this.debugEnabled) return
    if (payload === null) {
      // eslint-disable-next-line no-console
      console.log(`[topology-editor] ${message}`)
    } else {
      // eslint-disable-next-line no-console
      console.log(`[topology-editor] ${message}`, payload)
    }
  }

  _shouldEnableDebug() {
    const host = window.location.hostname || ""
    return host === "localhost" || host === "127.0.0.1" || host.endsWith(".local")
  }

  _toggleOverCapacityClass(cell, isOver) {
    if (isOver) {
      cell.classList.add("over-capacity")
      cell.title = "Over capacity"
    } else {
      cell.classList.remove("over-capacity")
      cell.removeAttribute("title")
    }
  }

  _overCapacitySwitchIds() {
    const bad = []
    ;(this.switchMap || new Map()).forEach(sw => {
      const used = Number(sw.used || 0)
      const total = Number(sw.total || 0)
      if (total >= 0 && used > total) bad.push(sw.id)
    })
    return bad
  }

  _checkPortCapacity(sw) {
    const used = Number(sw.used || 0)
    const total = Number(sw.total || 0)
    if (used > total) {
      return `Not enough ${sw.portSpeed || "ports"}`
    }
    return ""
  }

  _checkPowerBudget(sw) {
    const usedPower = Number(sw.usedPower || 0)
    if (sw.totalPower === null || sw.totalPower === undefined) return ""
    const totalPower = Number(sw.totalPower)
    if (!Number.isFinite(totalPower)) return ""
    if (usedPower > totalPower) {
      return "Insufficient power budget"
    }
    return ""
  }

  _checkPoeCompatibility(ap, sw) {
    if (this._isPowerAdapter(ap.powerSupply)) return ""
    const need = ap.poeStandard || ""
    const support = sw.poeStandard || ""

    const rank = { "802.3af": 1, "802.3at": 2, "802.3bt": 3 }

    if (rank[need] > (rank[support] || 0)) {
      return `PoE standard not supported (needs ${need})`
    }
    return ""
  }

  _isPowerAdapter(value) {
    return value?.toString().toLowerCase().includes("adapter")
  }

  _recalcFromRows() {
    this.switchMap.forEach(sw => { sw.used = 0; sw.usedPower = 0 })
    this.rowTargets.forEach(tr => {
      const swId = tr.dataset.currentSwitchId
      if (!swId || !this.switchMap.has(swId)) return
      const sw = this.switchMap.get(swId)
      sw.used += 1
      if (!this._isPowerAdapter(tr.dataset.apPowerSupply)) {
        sw.usedPower += Number(tr.dataset.apPower || 0)
      }
    })
  }

  _updateRowUI(tr) {
    const rawSwId = tr.dataset.currentSwitchId || ""
    const swId = rawSwId === "__AUTO__" ? "" : rawSwId
    tr.dataset.currentSwitchId = swId
    const label = tr.querySelector('[data-topology-editor-target="switchLabel"]')
    const switchModel = tr.querySelector('[data-topology-editor-target="switchModel"]')
    const dropdownText = tr.querySelector('[data-dropdown-input-target="text"]')
    const portCell = tr.querySelector('[data-topology-editor-target="portCell"]')
    const portAlert = tr.querySelector('[data-topology-editor-target="portAlert"]')
    const powerAlert = tr.querySelector('[data-topology-editor-target="powerAlert"]')
    const apNeed = tr.dataset.apPoe || ""

    const displaySwitchId = swId || "-"
    if (label) {
      label.textContent = displaySwitchId
      label.classList.toggle("muted", !swId)
    }
    if (dropdownText) dropdownText.textContent = displaySwitchId

    if (!swId || !this.switchMap.has(swId)) {
      if (switchModel) switchModel.textContent = "-"
      if (portCell) { portCell.textContent = "-"; this._toggleOverCapacityClass(portCell, false) }
      if (portAlert) {
        portAlert.textContent = "-"
        portAlert.classList.remove("port-alert")
      }
      if (powerAlert) {
        powerAlert.textContent = "-"
        powerAlert.classList.remove("power-alert")
      }
      return
    }

    const sw = this.switchMap.get(swId)
    if (switchModel) switchModel.textContent = sw.model || "-"
    if (portCell) {
      const usage = this._portUsageForRow(tr, swId)
      portCell.textContent = `${usage.used}/${usage.total}`
      this._toggleOverCapacityClass(portCell, usage.used > usage.total)
    }
    if (portAlert) {
      const apSupply = tr.dataset.apPowerSupply || ""
      const portErr = this._checkPortCapacity(sw)
      const poeErr = this._checkPoeCompatibility({ poeStandard: apNeed, powerSupply: apSupply }, sw)
      const alertMsg = poeErr || portErr
      portAlert.textContent = alertMsg || "-"
      portAlert.classList.toggle("port-alert", Boolean(alertMsg))
    }
    if (powerAlert) {
      const powerErr = this._checkPowerBudget(sw)
      powerAlert.textContent = powerErr || "-"
      powerAlert.classList.toggle("power-alert", Boolean(powerErr))
    }
  }

  _validateAll() {
    const errors = []
    this._recalcFromRows()
    this.rowTargets.forEach(tr => {
      const swId = tr.dataset.currentSwitchId
      if (!swId || !this.switchMap.has(swId)) return
      const sw = this.switchMap.get(swId)
      const apNeed = tr.dataset.apPoe || ""
      const apSupply = tr.dataset.apPowerSupply || ""

      const poeErr = this._checkPoeCompatibility({ poeStandard: apNeed, powerSupply: apSupply }, sw)
      const portErr = this._checkPortCapacity(sw)
      const pwrErr = this._checkPowerBudget(sw)

      if (poeErr) errors.push(`${tr.dataset.apId} -> ${sw.id}: ${poeErr}`)
      if (portErr) errors.push(`${tr.dataset.apId} -> ${sw.id}: ${portErr}`)
      if (pwrErr) errors.push(`${tr.dataset.apId} -> ${sw.id}: ${pwrErr}`)
    })
    return errors
  }

  _showServerResult(payload) {
    const forced = Array.isArray(payload.forced) ? payload.forced : []
    const errors = Array.isArray(payload.errors) ? payload.errors : []

    forced.forEach(f => {
      const tr = this.rowTargets.find(r => r.dataset.apId === f.ap_id)
      if (!tr) return
      tr.dataset.currentSwitchId = f.to || tr.dataset.currentSwitchId
      tr.classList.add("forced-assignment")
      const portAlert = tr.querySelector('[data-topology-editor-target="portAlert"]')
      if (portAlert) {
        const reason = f.reason ? `: ${f.reason}` : ""
        portAlert.textContent = `Forced to ${f.to}${reason}`
      }
      this._updateRowUI(tr)
    })
    if (errors.length) this.setStatus(errors[0], "error", { toast: true })
    else if (forced.length) this.setStatus(`Forced: ${forced[0].ap_id} -> ${forced[0].to}`, "info", { toast: true })

    if (errors.length) this.setStatus(errors.join("; "))
    this.setEditing(false)
  }

  _portUsageForRow(tr, swId) {
    const total = Number(this.switchMap.get(swId)?.total || 0)
    let used = 0
    for (const row of this.rowTargets) {
      if (row.dataset.currentSwitchId === swId) {
        used += 1
      }
      if (row === tr) break
    }
    return { used, total }
  }

  _clearHash() {
    if (window.location.hash) {
      history.replaceState(null, document.title, window.location.pathname + window.location.search)
    }
  }

  buildSwitchMap(list) {
    return new Map((list || []).map(s => {
      const id = String(s.id)
      return [id, {
        ...s,
        id,
        model: s.model || "",
        poeStandard: s.poeStandard || "",
        portSpeed: s.portSpeed || "1Gbps",
        used: Number(s.used || 0),
        total: Number(s.total || 0),
        usedPower: Number(s.usedPower || 0),
        totalPower: (s.totalPower === null || s.totalPower === undefined)
          ? null
          : Number(s.totalPower)
      }]
    }))
  }
}
